import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { AppBar, Toolbar, Typography, Box, IconButton, Menu, MenuItem, Avatar, Badge, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import VideocamIcon from '@mui/icons-material/Videocam';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import HistoryIcon from '@mui/icons-material/History';
import ViewCarouselOutlinedIcon from '@mui/icons-material/ViewCarouselOutlined';
import axios from 'axios';
import socket from '../socket';
import { useWindows } from '../contexts/WindowContext';
import { alpha } from '@mui/material/styles';
import {
  getInitialTaskSwitcherIndex,
  getTaskSwitcherWindows,
  moveTaskSwitcherIndex,
} from './windowLayerPolicy';
import { getDefaultFileManagerEntryPath } from '../utils/fileManagerEntryPolicy';

const TopBar = ({
  onOpenFriends,
  onOpenRooms,
  onOpenNotifications,
  unreadNotificationCount = 0,
  chatPreview = null,
  onChatPreviewClick = () => {},
  chatSidebarMode = 'none',
  aiPanelOpen = false,
  onToggleAi = () => {},
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    openWindows,
    taskbarOrder,
    taskbarWindows,
    focusWindow,
    showDesktop,
    toggleMinimize,
    focusedContext,
    openAppWindow,
    openFileWindowByPath,
    openFolderWindowByPath,
    fileManagerPath,
    setFileManagerPath,
    recoveryOffer,
    dismissRecoveryOffer,
    recordLastWork,
  } = useWindows();

  const [user, setUser] = useState(JSON.parse(localStorage.getItem('user')) || { username: 'USER', role: 'USER' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [navigationMenuAnchorEl, setNavigationMenuAnchorEl] = useState(null);
  const [folderMenuAnchorEl, setFolderMenuAnchorEl] = useState(null);
  const [fileMenuAnchorEl, setFileMenuAnchorEl] = useState(null);
  const [appMenuAnchorEl, setAppMenuAnchorEl] = useState(null);
  const [chatMenuAnchorEl, setChatMenuAnchorEl] = useState(null);
  const [appOpenMode, setAppOpenMode] = useState(localStorage.getItem('platform_app_open_mode') || 'window');
  const [taskSwitcherOpen, setTaskSwitcherOpen] = useState(false);
  const [taskSwitcherIndex, setTaskSwitcherIndex] = useState(0);
  const [taskSwitcherSessionIds, setTaskSwitcherSessionIds] = useState([]);
  const [taskSwitcherFlash, setTaskSwitcherFlash] = useState(false);
  const taskSwitcherFlashTimerRef = useRef(null);

  const [profileOpen, setProfileOpen] = useState(false);
  const [creseroProfile, setCreseroProfile] = useState(null);
  const [creseroError, setCreseroError] = useState('');
  useEffect(() => {
    if (!profileOpen) return undefined;
    let active = true; setCreseroProfile(null); setCreseroError('');
    axios.get('/api/cresero/profile', { withCredentials: true }).then(({ data }) => {
      if (active) setCreseroProfile(data);
    }).catch(() => { if (active) setCreseroError('계정 연결 상태를 확인하지 못했습니다. 잠시 후 다시 열어 주세요.'); });
    return () => { active = false; };
  }, [profileOpen, user.userUid]);

  const [accessHistoryOpen, setAccessHistoryOpen] = useState(false);
  const [accessHistory, setAccessHistory] = useState([]);
  const [accessHistoryError, setAccessHistoryError] = useState('');
  const [profileNickname, setProfileNickname] = useState(user.nickname || user.displayName || user.username || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const loadAccessHistory = useCallback(async () => {
    try {
      const response = await axios.get('/api/access-history?limit=100', { withCredentials: true });
      setAccessHistory(response.data?.events || []);
      setAccessHistoryError('');
    } catch (error) {
      setAccessHistoryError('접근 기록을 불러오지 못했습니다.');
    }
  }, []);

  useEffect(() => {
    if (!accessHistoryOpen) return undefined;
    loadAccessHistory();
    const onNotice = (notice) => { if (notice?.type === 'account_access') loadAccessHistory(); };
    socket.on('notification:new', onNotice);
    return () => socket.off('notification:new', onNotice);
  }, [accessHistoryOpen, loadAccessHistory]);

  useEffect(() => {
    const handleStorageChange = () => setUser(JSON.parse(localStorage.getItem('user')) || { username: 'USER', role: 'USER' });
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('nas:user-updated', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('nas:user-updated', handleStorageChange);
    };
  }, []);

  useEffect(() => () => {
    if (taskSwitcherFlashTimerRef.current) window.clearTimeout(taskSwitcherFlashTimerRef.current);
  }, []);

  const taskSwitcherWindows = useMemo(
    () => getTaskSwitcherWindows(openWindows, taskbarOrder),
    [openWindows, taskbarOrder]
  );

  const flashTaskSwitcherButton = useCallback(() => {
    if (taskSwitcherFlashTimerRef.current) window.clearTimeout(taskSwitcherFlashTimerRef.current);
    setTaskSwitcherFlash(false);
    window.requestAnimationFrame(() => {
      setTaskSwitcherFlash(true);
      taskSwitcherFlashTimerRef.current = window.setTimeout(() => setTaskSwitcherFlash(false), 420);
    });
  }, []);

  const closeTaskSwitcher = useCallback((flash = false) => {
    setTaskSwitcherOpen(false);
    setTaskSwitcherSessionIds([]);
    if (flash) flashTaskSwitcherButton();
  }, [flashTaskSwitcherButton]);

  const activateTaskWindow = useCallback((win) => {
    if (!win) return;
    if (win.winType === 'file' || win.winType === 'folder') navigate('/nas');
    if (win.winType === 'app') navigate('/platform');
    focusWindow(win.id);
    flashTaskSwitcherButton();
  }, [flashTaskSwitcherButton, focusWindow, navigate]);

  const toggleTaskSwitcher = () => {
    if (taskSwitcherOpen) {
      closeTaskSwitcher(true);
      return;
    }
    const sessionIds = taskSwitcherWindows.map((win) => win.id);
    const initialIndex = getInitialTaskSwitcherIndex(taskSwitcherWindows, focusedContext);
    setTaskSwitcherSessionIds(sessionIds);
    setTaskSwitcherIndex(initialIndex);
    setTaskSwitcherOpen(true);
    activateTaskWindow(taskSwitcherWindows[initialIndex]);
  };

  const taskSwitcherSessionWindows = useMemo(
    () => taskSwitcherSessionIds.map((id) => openWindows.find((win) => win.id === id)).filter(Boolean),
    [openWindows, taskSwitcherSessionIds]
  );

  useEffect(() => {
    if (!taskSwitcherOpen) return undefined;

    const handleTaskSwitcherKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeTaskSwitcher(true);
        return;
      }
      if (event.key === 'Tab') {
        event.preventDefault();
        event.stopPropagation();
        const direction = event.shiftKey ? -1 : 1;
        const nextIndex = moveTaskSwitcherIndex(taskSwitcherIndex, taskSwitcherSessionWindows.length, direction);
        setTaskSwitcherIndex(nextIndex);
        activateTaskWindow(taskSwitcherSessionWindows[nextIndex]);
      }
    };

    window.addEventListener('keydown', handleTaskSwitcherKeyDown, true);
    return () => window.removeEventListener('keydown', handleTaskSwitcherKeyDown, true);
  }, [activateTaskWindow, closeTaskSwitcher, taskSwitcherOpen, taskSwitcherSessionWindows, taskSwitcherIndex]);

  useEffect(() => {
    const handleSettingsChange = () => setAppOpenMode(localStorage.getItem('platform_app_open_mode') || 'window');
    window.addEventListener('nas_settings_changed', handleSettingsChange);
    window.addEventListener('storage', handleSettingsChange);
    return () => {
      window.removeEventListener('nas_settings_changed', handleSettingsChange);
      window.removeEventListener('storage', handleSettingsChange);
    };
  }, []);

  const getPageTitle = (path) => {
    if (path.startsWith('/nas')) return '파일 탐색기';
    if (path.startsWith('/settings')) return '시스템 설정';
    return '바탕화면';
  };

  const handleLogout = async () => {
    try {
      await axios.post('/api/logout', {}, { withCredentials: true });
    } catch (err) {
      // Clear local state even if the server session has already expired.
    }
    localStorage.removeItem('user');
    localStorage.removeItem('nas_session_left_at');
    window.dispatchEvent(new Event('nas:user-updated'));
    window.location.replace('/login');
  };

  const handlePasswordChange = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) return alert("모든 필드를 입력해주세요.");
    if (newPassword !== confirmPassword) return alert("새 비밀번호가 일치하지 않습니다.");
    if (newPassword.length < 10) return alert("비밀번호는 최소 10자 이상이어야 합니다.");

    try {
      const res = await axios.put('/api/users/password', {
        id: user.userUid || user.loginId || user.id || user.username,
        currentPassword,
        newPassword
      }, { withCredentials: true });

      if (res.data.success) {
        alert("비밀번호가 성공적으로 변경되었습니다! 다시 로그인해주세요.");
        handleLogout();
      }
    } catch (err) {
      alert(err.response?.data?.error || "비밀번호 변경에 실패했습니다.");
    }
  };

  const handleNicknameChange = async () => {
    const nickname = profileNickname.trim();
    if (nickname.length < 2) return alert('닉네임은 2자 이상 입력해주세요.');
    try {
      const response = await axios.put('/api/users/profile', { nickname }, { withCredentials: true });
      const nextUser = response.data?.user;
      if (!nextUser) return;
      localStorage.setItem('user', JSON.stringify(nextUser));
      setUser(nextUser);
      window.dispatchEvent(new Event('nas:user-updated'));
      alert('닉네임이 변경되었습니다.');
    } catch (err) {
      alert(err.response?.data?.error || '닉네임을 변경하지 못했습니다.');
    }
  };

  const taskWindows = taskbarWindows.filter(Boolean).slice().reverse();
  const minimizedFolders = appOpenMode === 'window' ? taskWindows.filter((w) => w.winType === 'folder') : [];
  const minimizedFiles = taskWindows.filter((w) => w.winType === 'file');
  const minimizedApps = taskWindows.filter((w) => w.winType === 'app');
  const minimizedChats = taskWindows.filter((w) => w.winType === 'chat');
  const canOpenBackup = user.role === 'MASTER' || user.Masters || user.globalAccess;
  const openDefaultFileManager = useCallback(() => {
    showDesktop();
    setFileManagerPath(getDefaultFileManagerEntryPath(user, fileManagerPath));
    navigate('/nas');
  }, [fileManagerPath, navigate, setFileManagerPath, showDesktop, user]);

  const navigationItems = [
    { id: 'desktop', title: '바탕화면', icon: SpaceDashboardIcon, action: () => goDesktop() },
    { id: 'files', title: '파일 관리자', icon: FolderIcon, action: openDefaultFileManager },
    { id: 'pc-sync', title: 'PC 연동', icon: DesktopWindowsIcon, action: () => { showDesktop(); navigate('/nas'); } },
    {
      id: 'meeting',
      title: '화상회의',
      icon: VideocamIcon,
      action: () => {
        if (appOpenMode === 'window') {
          openAppWindow({
            id: 'meeting',
            title: '화상회의',
            winType: 'app',
            width: 920,
            height: 640,
            payload: {}
          });
        } else {
          window.dispatchEvent(new CustomEvent('platform:open-app', { detail: { id: 'meeting' } }));
          navigate('/platform');
        }
      }
    },
    { id: 'settings', title: '설정', icon: SettingsIcon, action: () => { showDesktop(); navigate('/settings'); } },
    ...(canOpenBackup ? [{ id: 'backup', title: '백업', icon: HistoryIcon, action: () => { showDesktop(); navigate('/nas/backup'); } }] : [])
  ];

  const handleTaskWindowClick = (win) => {
    if (!win) return;
    if (!win.isMinimized && focusedContext === win.id) {
      toggleMinimize(win.id);
      return;
    }
    if (win.winType === 'file' || win.winType === 'folder') navigate('/nas');
    if (win.winType === 'app') navigate('/platform');
    focusWindow(win.id);
  };

  const handleNavigationSelect = (item) => {
    setNavigationMenuAnchorEl(null);
    if (item.id === 'files') recordLastWork({ type: 'folder', path: fileManagerPath || '/', label: '파일 관리자' });
    else if (item.id !== 'desktop') dismissRecoveryOffer();
    item.action();
  };

  const handleResumeLastWork = async () => {
    if (!recoveryOffer) return;
    if (recoveryOffer.type === 'file') {
      navigate('/nas');
      await openFileWindowByPath(recoveryOffer.path, recoveryOffer.label);
      return;
    }
    if (recoveryOffer.type === 'folder') {
      navigate('/nas');
      openFolderWindowByPath(recoveryOffer.path, recoveryOffer.label);
      return;
    }
    const appSettings = {
      'note-studio': { width: 1180, height: 780 },
      'document-workspace': { width: 1100, height: 760 },
      'document-studio': { width: 1120, height: 760 },
      meeting: { width: 920, height: 640 },
    }[recoveryOffer.appId];
    if (appSettings) {
      navigate('/platform');
      openAppWindow({ id: recoveryOffer.appId, title: recoveryOffer.label, ...appSettings });
    }
  };

  const goDesktop = () => {
    setTaskSwitcherOpen(false);
    showDesktop();
    window.dispatchEvent(new Event('platform:show-desktop'));
    navigate('/platform');
  };

  return (
    <>
      <AppBar position="fixed" elevation={0} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.94), backdropFilter: 'blur(14px)', color: 'text.primary', borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
        <Toolbar size="small" sx={{ minHeight: '48px !important', gap: 1, minWidth: 0, overflow: 'hidden' }}>
          <IconButton
            size="small"
            aria-label={taskSwitcherOpen ? '웹 내부 창 전환 모드 끄기' : '웹 내부 창 전환 모드 켜기'}
            aria-pressed={taskSwitcherOpen}
            title={taskSwitcherOpen ? '웹 내부 창 전환 모드 끄기' : '웹 내부 창 전환 모드 켜기'}
            onClick={toggleTaskSwitcher}
            sx={{
              width: 24,
              height: 28,
              border: (theme) => `1px solid ${taskSwitcherOpen ? theme.palette.error.main : theme.palette.divider}`,
              color: taskSwitcherOpen ? 'error.main' : 'text.secondary',
              bgcolor: taskSwitcherOpen ? (theme) => alpha(theme.palette.error.main, 0.08) : 'transparent',
              ...(taskSwitcherFlash ? {
                animation: 'nasTaskSwitchFlash 420ms ease-out 1',
                '@keyframes nasTaskSwitchFlash': {
                  '0%': { bgcolor: 'error.main', color: 'error.contrastText', borderColor: 'error.main' },
                  '55%': { bgcolor: 'error.main', color: 'error.contrastText', borderColor: 'error.main' },
                  '100%': { bgcolor: 'transparent', color: 'error.main', borderColor: 'error.main' },
                },
              } : {}),
            }}
          >
            <ViewCarouselOutlinedIcon sx={{ fontSize: 15 }} />
          </IconButton>
          {taskSwitcherOpen && <Chip size="small" color="error" variant="outlined" label="웹 창 전환 · Tab / Shift+Tab" title="웹 창 전환 · Tab / Shift+Tab" sx={{ height: 26, flex: '0 1 auto', maxWidth: { xs: 120, sm: 220 } }} />}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Box onClick={goDesktop} sx={{ cursor: 'pointer' }}>
            <Typography variant="h6" sx={{ fontWeight: 900, fontSize: '0.98rem', whiteSpace: 'nowrap' }}>NAS</Typography>
            </Box>
          </Box>
          <Box
            onClick={(e) => setNavigationMenuAnchorEl(e.currentTarget)}
            sx={{
              ml: 2,
              px: 1.2,
              py: 0.5,
              display: 'flex',
              alignItems: 'center',
              gap: 0.8,
              borderRadius: 1,
              backgroundColor: 'transparent',
              border: (theme) => `1px solid ${theme.palette.divider}`,
              cursor: 'pointer',
              userSelect: 'none',
              '&:hover': {
                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.06),
              },
            }}
          >
            <SpaceDashboardIcon sx={{ fontSize: 16, color: 'primary.main' }} />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 800,
                color: 'text.primary',
                lineHeight: 1,
              }}
            >
              {getPageTitle(location.pathname)}
            </Typography>
          </Box>
          <Menu
            anchorEl={navigationMenuAnchorEl}
            open={Boolean(navigationMenuAnchorEl)}
            onClose={() => setNavigationMenuAnchorEl(null)}
            PaperProps={{ elevation: 4, sx: { mt: 1, minWidth: 220 } }}
          >
            {navigationItems.map((item) => {
              const Icon = item.icon;
              return (
                <MenuItem
                  key={item.id}
                  onClick={() => handleNavigationSelect(item)}
                  selected={
                    (item.id === 'desktop' && location.pathname.startsWith('/platform')) ||
                    (item.id === 'files' && location.pathname.startsWith('/nas')) ||
                    (item.id === 'settings' && location.pathname.startsWith('/settings'))
                  }
                  sx={{ gap: 1.25, py: 1 }}
                >
                  <Icon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>{item.title}</Typography>
                </MenuItem>
              );
            })}
          </Menu>

          {minimizedFolders.length > 0 && (
            <>
              <Chip
                label={minimizedFolders.length === 1 ? (minimizedFolders[0]?.name || '열린 폴더') : `열린 폴더 ${minimizedFolders.length}`}
                size="small"
                icon={<FolderIcon sx={{ fontSize: 16 }} />}
                onClick={(e) => {
                  if (minimizedFolders.length === 1) {
                    handleTaskWindowClick(minimizedFolders[0]);
                  } else {
                    setFolderMenuAnchorEl(e.currentTarget);
                  }
                }}
                title={minimizedFolders.length === 1 ? (minimizedFolders[0]?.name || '열린 폴더') : `열린 폴더 ${minimizedFolders.length}`}
                sx={{
                  ml: 1,
                  maxWidth: { xs: 112, md: 190 },
                  display: { xs: 'none', md: 'inline-flex' },
                  backgroundColor: 'action.hover',
                  color: 'text.primary',
                  cursor: 'pointer'
                }}
              />
              {minimizedFolders.length > 1 && (
                <Menu
                  anchorEl={folderMenuAnchorEl}
                  open={Boolean(folderMenuAnchorEl)}
                  onClose={() => setFolderMenuAnchorEl(null)}
                  PaperProps={{ elevation: 0, sx: { mt: 1, minWidth: 260, borderRadius: 1 } }}
                >
                  {minimizedFolders.map((win) => (
                    <MenuItem
                      key={win.id}
                      onClick={() => {
                        setFolderMenuAnchorEl(null);
                        handleTaskWindowClick(win);
                      }}
                      sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
                    >
                      <FolderIcon fontSize="small" sx={{ mt: 0.2, color: 'warning.main' }} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography className="nas-dynamic-label" title={win.name} variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                          {win.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: 'block',
                            maxWidth: 180,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {win.currentPath || win.basePath || '/'}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Menu>
              )}
            </>
          )}

          {minimizedFiles.length > 0 && (
            <>
              <Chip
                label={minimizedFiles.length === 1 ? (minimizedFiles[0]?.name || '열린 파일') : `열린 파일 ${minimizedFiles.length}`}
                size="small"
                icon={<InsertDriveFileIcon sx={{ fontSize: 16 }} />}
                onClick={(e) => {
                  if (minimizedFiles.length === 1) {
                    handleTaskWindowClick(minimizedFiles[0]);
                  } else {
                    setFileMenuAnchorEl(e.currentTarget);
                  }
                }}
                title={minimizedFiles.length === 1 ? (minimizedFiles[0]?.name || '열린 파일') : `열린 파일 ${minimizedFiles.length}`}
                sx={{
                  ml: 1,
                  maxWidth: { xs: 112, md: 190 },
                  display: { xs: 'none', md: 'inline-flex' },
                  backgroundColor: 'action.hover',
                  color: 'text.primary',
                  cursor: 'pointer'
                }}
              />
              {minimizedFiles.length > 1 && (
                <Menu
                  anchorEl={fileMenuAnchorEl}
                  open={Boolean(fileMenuAnchorEl)}
                  onClose={() => setFileMenuAnchorEl(null)}
                  PaperProps={{ elevation: 0, sx: { mt: 1, minWidth: 260, borderRadius: 1 } }}
                >
                  {minimizedFiles.map((win) => (
                    <MenuItem
                      key={win.id}
                      onClick={() => {
                        setFileMenuAnchorEl(null);
                        handleTaskWindowClick(win);
                      }}
                      sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
                    >
                      <InsertDriveFileIcon fontSize="small" sx={{ mt: 0.2, color: 'text.secondary' }} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography className="nas-dynamic-label" title={win.name} variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                          {win.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: 'block',
                            maxWidth: 180,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {win.fullPath || ''}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Menu>
              )}
            </>
          )}

          {minimizedApps.length > 0 && (
            <>
              <Chip
                label={minimizedApps.length === 1 ? (minimizedApps[0]?.name || '실행 중인 앱') : `실행 앱 ${minimizedApps.length}`}
                size="small"
                icon={<VideocamIcon sx={{ fontSize: 16 }} />}
                onClick={(e) => {
                  if (minimizedApps.length === 1) {
                    handleTaskWindowClick(minimizedApps[0]);
                  } else {
                    setAppMenuAnchorEl(e.currentTarget);
                  }
                }}
                title={minimizedApps.length === 1 ? (minimizedApps[0]?.name || '실행 중인 앱') : `실행 앱 ${minimizedApps.length}`}
                sx={{
                  ml: 1,
                  maxWidth: { xs: 112, md: 190 },
                  display: { xs: 'none', md: 'inline-flex' },
                  backgroundColor: 'action.hover',
                  color: 'text.primary',
                  cursor: 'pointer'
                }}
              />
              {minimizedApps.length > 1 && (
                <Menu
                  anchorEl={appMenuAnchorEl}
                  open={Boolean(appMenuAnchorEl)}
                  onClose={() => setAppMenuAnchorEl(null)}
                  PaperProps={{ elevation: 0, sx: { mt: 1, minWidth: 260, borderRadius: 1 } }}
                >
                  {minimizedApps.map((win) => (
                    <MenuItem
                      key={win.id}
                      onClick={() => {
                        setAppMenuAnchorEl(null);
                        handleTaskWindowClick(win);
                      }}
                      sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
                    >
                      <VideocamIcon fontSize="small" sx={{ mt: 0.2, color: 'info.main' }} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography className="nas-dynamic-label" title={win.name} variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                          {win.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: 'block',
                            maxWidth: 180,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          실행 중
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Menu>
              )}
            </>
          )}

          {minimizedChats.length > 0 && (
            <>
              <Chip
                label={minimizedChats.length === 1 ? (minimizedChats[0]?.name || '열린 채팅') : `열린 채팅 ${minimizedChats.length}`}
                size="small"
                icon={<ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />}
                onClick={(e) => {
                  if (minimizedChats.length === 1) {
                    handleTaskWindowClick(minimizedChats[0]);
                  } else {
                    setChatMenuAnchorEl(e.currentTarget);
                  }
                }}
                title={minimizedChats.length === 1 ? (minimizedChats[0]?.name || '열린 채팅') : `열린 채팅 ${minimizedChats.length}`}
                sx={{
                  ml: 1,
                  maxWidth: { xs: 112, md: 190 },
                  display: { xs: 'none', md: 'inline-flex' },
                  backgroundColor: 'action.hover',
                  color: 'text.primary',
                  cursor: 'pointer'
                }}
              />
              {minimizedChats.length > 1 && (
                <Menu
                  anchorEl={chatMenuAnchorEl}
                  open={Boolean(chatMenuAnchorEl)}
                  onClose={() => setChatMenuAnchorEl(null)}
                  PaperProps={{ elevation: 0, sx: { mt: 1, minWidth: 260, borderRadius: 1 } }}
                >
                  {minimizedChats.map((win) => (
                    <MenuItem
                      key={win.id}
                      onClick={() => {
                        setChatMenuAnchorEl(null);
                        handleTaskWindowClick(win);
                      }}
                      sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
                    >
                      <ChatBubbleOutlineIcon fontSize="small" sx={{ mt: 0.2, color: 'primary.main' }} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography className="nas-dynamic-label" title={win.name} variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                          {win.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: 'block',
                            maxWidth: 180,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          @{win.chatUsername || win.name}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Menu>
              )}
            </>
          )}

          <Box sx={{ flexGrow: 1 }} />

          {chatPreview && (
            <Chip
              size="small"
              clickable
              onClick={() => { dismissRecoveryOffer(); onChatPreviewClick(); }}
              icon={<ChatBubbleOutlineIcon sx={{ fontSize: 16, color: '#fff !important' }} />}
              label={chatPreview.text}
              sx={{
                mr: 1,
                maxWidth: { xs: 180, sm: 360 },
                backgroundColor: 'primary.main',
                color: '#fff',
                borderRadius: 1.5,
                '& .MuiChip-label': {
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontWeight: 700,
                },
              }}
            />
          )}

          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
            <IconButton
              aria-label={aiPanelOpen ? 'AI 에이전트 닫기' : 'AI 에이전트 열기'}
              aria-pressed={aiPanelOpen}
              onClick={() => { dismissRecoveryOffer(); onToggleAi(); }}
              size="small"
              sx={{ color: 'primary.main', bgcolor: aiPanelOpen ? 'action.selected' : 'action.hover' }}
              title={aiPanelOpen ? 'AI 에이전트 닫기' : 'AI 에이전트 열기'}
            >
              <SmartToyIcon fontSize="small" />
            </IconButton>

            <IconButton aria-label="알림 열기" title="알림" onClick={() => { dismissRecoveryOffer(); onOpenNotifications(); }} size="small" sx={{ color: 'text.primary', bgcolor: 'action.hover' }}>
              <Badge
                color="error"
                badgeContent={unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                invisible={!unreadNotificationCount}
              >
                <NotificationsNoneIcon fontSize="small" />
              </Badge>
            </IconButton>

            <IconButton
              aria-label="채팅방 열기"
              title="채팅방"
              onClick={() => { dismissRecoveryOffer(); onOpenRooms(); }}
              size="small"
              sx={{
                color: 'text.primary',
                backgroundColor: chatSidebarMode === 'rooms'
                  ? 'action.selected'
                  : 'transparent',
              }}
            >
              <ChatBubbleOutlineIcon fontSize="small" />
            </IconButton>

            <IconButton
              aria-label="친구 관리 열기"
              title="친구 관리"
              onClick={() => { dismissRecoveryOffer(); onOpenFriends(); }}
              size="small"
              sx={{
                color: 'text.primary',
                backgroundColor: chatSidebarMode === 'friends'
                  ? 'action.selected'
                  : 'transparent',
              }}
            >
              <ManageAccountsIcon fontSize="small" />
            </IconButton>

            <IconButton aria-label="파일 관리자 열기" title="파일 관리자" onClick={openDefaultFileManager} size="small" sx={{ color: 'text.primary' }}> <FolderIcon fontSize="small" /> </IconButton>
            <IconButton aria-label="접근 기록 열기" title="접근 기록" onClick={() => setAccessHistoryOpen(true)} size="small" sx={{ color: 'text.primary' }}> <HistoryIcon fontSize="small" /> </IconButton>
            <IconButton aria-label="설정 열기" title="설정" onClick={() => { showDesktop(); navigate('/settings'); }} size="small" sx={{ color: 'text.primary' }}> <SettingsIcon fontSize="small" /> </IconButton>

            <IconButton aria-label="계정 메뉴 열기" title="계정 메뉴" onClick={(e) => setAnchorEl(e.currentTarget)} size="small">
              <Avatar sx={{ width: 28, height: 28, fontSize: '0.8rem', bgcolor: 'primary.main', fontWeight: 'bold' }}>
                {(user.nickname || user.displayName || user.username)?.[0]?.toUpperCase()}
              </Avatar>
            </IconButton>
          </Box>

          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)} PaperProps={{ elevation: 0, sx: { mt: 1, minWidth: 150, borderRadius: 1 } }}>
            <Box sx={{ px: 2, py: 1, outline: 'none' }}>
              <Typography className="nas-dynamic-label" title={user.nickname || user.displayName || user.username} variant="subtitle2" sx={{ fontWeight: 'bold', maxWidth: 220 }}>{user.nickname || user.displayName || user.username}</Typography>
              <Typography variant="caption" color="text.secondary">
                {(user.displayName && user.displayName !== user.username ? `@${user.username} · ` : '')}{user.role}
              </Typography>
            </Box>
            <Box sx={{ my: 0.5, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }} />

            <MenuItem onClick={() => { setAnchorEl(null); setProfileNickname(user.nickname || user.displayName || user.username || ''); setProfileOpen(true); }}>
              <ManageAccountsIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} /> 내 정보 수정
            </MenuItem>

            <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
              <LogoutIcon fontSize="small" sx={{ mr: 1.5 }} /> 로그아웃
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {recoveryOffer && (
        <Box sx={{ position: 'fixed', top: 56, right: 16, zIndex: (theme) => theme.zIndex.drawer + 2, width: 'min(320px, calc(100vw - 32px))', p: 1.25, border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 1, bgcolor: 'background.paper', boxShadow: 4 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>로그인 전 마지막 작업</Typography>
          <Typography variant="body2" title={recoveryOffer.label} sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mb: 1 }}>{recoveryOffer.label}</Typography>
          <Button fullWidth size="small" variant="contained" startIcon={<HistoryIcon />} onClick={handleResumeLastWork}>이전 작업 복구</Button>
        </Box>
      )}

      <Dialog open={accessHistoryOpen} onClose={() => setAccessHistoryOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>내 저장공간 접근 기록</DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '60vh' }}>
          {accessHistoryError && <Typography color="error" variant="body2">{accessHistoryError}</Typography>}
          {!accessHistoryError && accessHistory.length === 0 && <Typography color="text.secondary" variant="body2">기록된 다른 사용자 접근이 없습니다.</Typography>}
          {accessHistory.map((entry) => (
            <Box key={entry.activityId} sx={{ py: 1, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
              <Typography variant="body2" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{entry.actorName || '다른 사용자'} · {entry.path || '/'}</Typography>
              <Typography variant="caption" color="text.secondary">{new Date(entry.at).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })} KST · {entry.type === 'folder-opened' ? '폴더 열람' : entry.type === 'share-downloaded' ? '공유 링크 다운로드' : entry.type}</Typography>
              {entry.type === 'share-downloaded' && (
                <Box component="details" sx={{ mt: 0.5 }}>
                  <Box component="summary" sx={{ cursor: 'pointer', fontSize: 12 }}>받은 파일 {entry.fileCount || entry.files?.length || 0}개 보기</Box>
                  {(entry.files || []).map((file, index) => <Typography key={`${entry.activityId}-${index}`} variant="caption" display="block" sx={{ pl: 1, overflowWrap: 'anywhere' }}>{file}</Typography>)}
                  {entry.filesTruncated && <Typography variant="caption" color="warning.main">파일이 많아 일부만 기록되었습니다.</Typography>}
                </Box>
              )}
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={loadAccessHistory}>새로고침</Button>
          <Button onClick={() => setAccessHistoryOpen(false)}>닫기</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={profileOpen} onClose={() => setProfileOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>내 정보 수정</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="아이디" value={user.username} disabled size="small" fullWidth />
            <TextField label="현재 권한" value={user.role} disabled size="small" fullWidth />
            <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 'bold', color: 'primary.main' }}>Cresero 계정·이메일</Typography>
            {creseroError && <Typography color="error" variant="body2">{creseroError}</Typography>}
            {!creseroProfile && !creseroError && <Typography variant="body2">연결 상태 확인 중…</Typography>}
            {creseroProfile && <>
              {creseroProfile.email ? <TextField label="직원 이메일" value={creseroProfile.email} size="small" fullWidth InputProps={{ readOnly: true }} helperText="예약된 회사 주소입니다. 실제 송수신은 메일 제공자 연결 후 이용할 수 있습니다." /> : <Typography variant="body2">{creseroProfile.linked ? 'Cresero 일반 계정 연결됨 · 직원 이메일은 직원 확인 후 부여됩니다.' : '연결된 Cresero 계정이 없습니다.'}</Typography>}
              <Button variant="outlined" onClick={() => window.location.assign('https://cresero.com/api/company-nas/start')}>{creseroProfile.linked ? 'Cresero 계정 열기' : 'Cresero 계정 연결'}</Button>
              {creseroProfile.email && <Button variant="outlined" href="https://cresero.com/mail" target="_blank" rel="noopener noreferrer">Cresero 메일</Button>}
            </>}


            <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 'bold', color: 'primary.main' }}>닉네임</Typography>
            <TextField label="닉네임" value={profileNickname} onChange={(e) => setProfileNickname(e.target.value)} size="small" fullWidth helperText="친구, 채팅, 회의에 표시되는 이름입니다." />
            <Button onClick={handleNicknameChange} variant="outlined">닉네임 저장</Button>

            <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 'bold', color: 'primary.main' }}>비밀번호 변경</Typography>
            <TextField label="현재 비밀번호" type="password" size="small" fullWidth value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
            <TextField label="새 비밀번호" type="password" size="small" fullWidth value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            <TextField label="새 비밀번호 확인" type="password" size="small" fullWidth value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setProfileOpen(false)} color="inherit">취소</Button>
          <Button onClick={handlePasswordChange} variant="contained" color="primary">비밀번호 변경</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TopBar;
